import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles, Info,
    FileText, Target, AlertTriangle, Lightbulb, Edit3
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo5LineaBase() {
    const navigate = useNavigate();
    const [tareaPostergada, setTareaPostergada] = useState('');
    const [actividadSustituta, setActividadSustituta] = useState('');
    const [costoPrincipal, setCostoPrincipal] = useState('');
    const [patronDominante, setPatronDominante] = useState('');
    const [tareaFaro, setTareaFaro] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [preloadedData, setPreloadedData] = useState(null);

    // Load draft and preload data from previous modules
    useEffect(() => {
        const loadData = async () => {
            try {
                // Load draft first
                const draftResponse = await axios.get(`${API_URL}/api/user/draft/linea_base`, {
                    withCredentials: true
                });
                
                if (draftResponse.data.data) {
                    const d = draftResponse.data.data;
                    if (d.tareaPostergada) setTareaPostergada(d.tareaPostergada);
                    if (d.actividadSustituta) setActividadSustituta(d.actividadSustituta);
                    if (d.costoPrincipal) setCostoPrincipal(d.costoPrincipal);
                    if (d.patronDominante) setPatronDominante(d.patronDominante);
                    if (d.tareaFaro) setTareaFaro(d.tareaFaro);
                }

                // Load preloaded suggestions from profile
                const profileResponse = await axios.get(`${API_URL}/api/user/profile`, {
                    withCredentials: true
                });
                
                if (profileResponse.data.ai_summaries?.length > 0) {
                    const summaries = profileResponse.data.ai_summaries;
                    const latestSummary = summaries[summaries.length - 1];
                    setPreloadedData({
                        patronDominante: latestSummary.patron_dominante || '',
                        conductaSustituta: latestSummary.conducta_sustituta_frecuente || ''
                    });
                    
                    // Pre-fill if draft is empty
                    if (!draftResponse.data.data?.patronDominante && latestSummary.patron_dominante) {
                        setPatronDominante(latestSummary.patron_dominante);
                    }
                    if (!draftResponse.data.data?.actividadSustituta && latestSummary.conducta_sustituta_frecuente) {
                        setActividadSustituta(latestSummary.conducta_sustituta_frecuente);
                    }
                }
                if (draftResponse.data.read_only) { setReadOnly(true); setSubmittedAt(draftResponse.data.submitted_at); }
            } catch (error) {
                // silent - data load failure handled gracefully
            } finally {
                setLoadingDraft(false);
            }
        };
        loadData();
    }, []);

    // Auto-save draft
    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { 
                    module_id: 'linea_base', 
                    data: { 
                        tareaPostergada, 
                        actividadSustituta, 
                        costoPrincipal, 
                        patronDominante, 
                        tareaFaro 
                    } 
                },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [tareaPostergada, actividadSustituta, costoPrincipal, patronDominante, tareaFaro]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [tareaPostergada, actividadSustituta, costoPrincipal, patronDominante, tareaFaro, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && tareaPostergada.trim().length > 0 && 
                      actividadSustituta.trim().length > 0 && 
                      costoPrincipal.trim().length > 0 && 
                      patronDominante.trim().length > 0 && 
                      tareaFaro.trim().length > 0;

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) {
            toast.error('Completa todos los campos requeridos');
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/linea-base/submit`,
                { 
                    tarea_postergada: tareaPostergada,
                    actividad_sustituta: actividadSustituta,
                    costo_principal: costoPrincipal,
                    patron_dominante: patronDominante,
                    tarea_faro: tareaFaro
                },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('¡Línea base SENDA completada!');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al enviar el módulo';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-2xl bg-white border border-[#DCE3E8] rounded-2xl shadow-lg animate-fade-in-up">
                    <CardContent className="pt-8">
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 bg-[#5FAF9D] rounded-full flex items-center justify-center mx-auto mb-6">
                                <Check className="h-10 w-10 text-white" />
                            </div>
                            <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">¡Capítulo 1 completado!</h2>
                            <p className="text-[#4A5B63]">Tu línea base SENDA está lista</p>
                        </div>
                        
                        {/* Baseline Summary */}
                        <div className="bg-[#F5F7F8] rounded-xl border border-[#DCE3E8] p-6 mb-6">
                            <h3 className="text-sm font-semibold text-[#3E5F6E] uppercase mb-4 flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                Tu línea base
                            </h3>
                            
                            <div className="space-y-4">
                                <div>
                                    <p className="text-xs text-[#7A8A93] uppercase mb-1">Tarea que más postergas</p>
                                    <p className="text-[#1F2A30]">{tareaPostergada}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#7A8A93] uppercase mb-1">Actividad sustituta frecuente</p>
                                    <p className="text-[#1F2A30]">{actividadSustituta}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#7A8A93] uppercase mb-1">Costo principal</p>
                                    <p className="text-[#1F2A30]">{costoPrincipal}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-[#7A8A93] uppercase mb-1">Patrón dominante</p>
                                    <p className="text-[#1F2A30] font-medium">{patronDominante}</p>
                                </div>
                                <div className="pt-4 border-t border-[#DCE3E8]">
                                    <p className="text-xs text-[#5FAF9D] uppercase mb-1 flex items-center gap-1">
                                        <Target className="h-3 w-3" />
                                        Tarea faro
                                    </p>
                                    <p className="text-[#1F2A30] font-medium">{tareaFaro}</p>
                                </div>
                            </div>
                        </div>
                        
                        {aiInsight && (
                            <div className="senda-ai-panel mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-2">Observación final</p>
                                        <p className="text-[#1F2A30] italic">"{aiInsight}"</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <p className="text-[#4A5B63] text-center mb-6">
                            Has consolidado tu perfil conductual del Capítulo 1. Esta información será tu punto de referencia para medir tu progreso.
                        </p>
                        
                        <div className="flex justify-center">
                            <div className="mb-6"><ModuleFeedback moduleId="linea_base" chapter={1} /></div>
                            <Link to="/usuario">
                                <Button className="senda-btn-primary" data-testid="back-to-dashboard-linea-base">
                                    Volver al inicio
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#3E5F6E]" data-testid="back-to-usuario-linea-base">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Línea base SENDA</h1>
                                <p className="text-sm text-[#4A5B63]">Consolida tu perfil del Capítulo 1</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {savingDraft && !readOnly && (
                                <span className="text-xs text-[#7A8A93] flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Guardando...
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="senda-module-container animate-fade-in-up">
                    <CardHeader className="pb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-[#3E5F6E] rounded-xl flex items-center justify-center">
                                <FileText className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#3E5F6E]">Tu resumen conductual</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Revisa y confirma tu línea base</p>
                            </div>
                        </div>
                        
                        <div className="p-4 bg-[#F5F7F8] rounded-lg border border-[#DCE3E8] flex items-start gap-3">
                            <Info className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                            <p className="text-sm text-[#4A5B63]">
                                Esta es una síntesis de lo que has descubierto en los módulos anteriores. 
                                Algunos campos vienen pre-cargados con sugerencias basadas en tus respuestas. 
                                Puedes editarlos antes de guardar.
                            </p>
                        </div>
                        
                        {preloadedData && (
                            <div className="mt-4 p-3 bg-[#5FAF9D]/10 rounded-lg border border-[#5FAF9D]/20 flex items-center gap-2">
                                <Lightbulb className="h-4 w-4 text-[#5FAF9D]" />
                                <p className="text-xs text-[#5FAF9D]">
                                    Algunos campos han sido pre-llenados con datos de tus módulos anteriores
                                </p>
                            </div>
                        )}
                    </CardHeader>
                    
                    <CardContent className="space-y-6">
                        {/* Tarea Postergada */}
                        <div className="space-y-2">
                            <Label className="senda-label flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-[#E8B27A]" />
                                Tarea que más postergas
                            </Label>
                            <Input
                                value={tareaPostergada}
                                onChange={(e) => setTareaPostergada(e.target.value)}
                                placeholder="¿Cuál es la tarea que consistentemente evitas?"
                                className="senda-input"
                                data-testid="tarea-postergada-input"
                            />
                        </div>

                        {/* Actividad Sustituta */}
                        <div className="space-y-2">
                            <Label className="senda-label flex items-center gap-2">
                                <Edit3 className="h-4 w-4 text-[#7A9BA8]" />
                                Actividad sustituta frecuente
                            </Label>
                            <Input
                                value={actividadSustituta}
                                onChange={(e) => setActividadSustituta(e.target.value)}
                                placeholder="¿Qué haces en lugar de la tarea?"
                                className="senda-input"
                                data-testid="actividad-sustituta-input"
                            />
                        </div>

                        {/* Costo Principal */}
                        <div className="space-y-2">
                            <Label className="senda-label">Costo principal de postergar</Label>
                            <Textarea
                                value={costoPrincipal}
                                onChange={(e) => setCostoPrincipal(e.target.value)}
                                placeholder="¿Cuál es la consecuencia más significativa de esta evitación?"
                                className="senda-input min-h-[80px]"
                                data-testid="costo-principal-input"
                            />
                        </div>

                        {/* Patrón Dominante */}
                        <div className="space-y-2">
                            <Label className="senda-label">Patrón dominante identificado</Label>
                            <Input
                                value={patronDominante}
                                onChange={(e) => setPatronDominante(e.target.value)}
                                placeholder="Ej: Evitación por perfeccionismo, Fatiga crónica, Saturación..."
                                className="senda-input"
                                data-testid="patron-dominante-input"
                            />
                        </div>

                        {/* Tarea Faro */}
                        <div className="space-y-2 p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                            <Label className="senda-label flex items-center gap-2 text-[#5FAF9D]">
                                <Target className="h-4 w-4" />
                                Tarea faro (tu objetivo principal)
                            </Label>
                            <Textarea
                                value={tareaFaro}
                                onChange={(e) => setTareaFaro(e.target.value)}
                                placeholder="Define una tarea específica y alcanzable que te gustaría abordar como punto de partida para romper tu patrón de evitación."
                                className="senda-input min-h-[100px] bg-white"
                                data-testid="tarea-faro-input"
                            />
                            <p className="text-xs text-[#5FAF9D]">
                                La tarea faro debe ser clara, específica y realizable. Será tu guía para los próximos capítulos.
                            </p>
                        </div>

                        {/* Submit */}
                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button
                                onClick={handleSubmit}
                                disabled={!canSubmit || loading}
                                className="senda-btn-accent"
                                data-testid="submit-linea-base-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Guardando línea base...
                                    </>
                                ) : (
                                    <>
                                        Completar Capítulo 1
                                        <Check className="h-4 w-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
